import { useState, useEffect } from 'react';
import api from '../api/client';
import './Admin.css';

function Admin() {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedError, setSelectedError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchErrors();
  }, []);

  const fetchErrors = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') params.type = filter;
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate).toISOString();

      const response = await api.get('/errors/', { params });
      setErrors(response.data);
    } catch (err) {
      console.error('Failed to fetch errors:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredErrors = errors.filter((error) => {
    if (searchQuery && !error.message.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const exportErrors = () => {
    const csv = [
      ['Type', 'Endpoint', 'Status', 'Message', 'User', 'Timestamp', 'Count'],
      ...filteredErrors.map((e) => [
        e.type,
        e.endpoint || 'N/A',
        e.status || 'N/A',
        e.message,
        e.username || 'Anonymous',
        e.timestamp,
        e.count
      ])
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api_errors_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleApplyFilters = () => {
    fetchErrors();
  };

  const handleResetFilters = () => {
    setFilter('all');
    setStartDate('');
    setEndDate('');
    setSearchQuery('');
    setTimeout(() => fetchErrors(), 0);
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Администрација</h1>
        <p className="muted">Извештаи за API грешки и исклучоци</p>
      </div>

      <div className="admin-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Пребарај по порака..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Филтрирај по тип:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">Сите</option>
            <option value="API_ERROR">API грешки</option>
            <option value="TIMEOUT">Timeout</option>
            <option value="EXCEPTION">Исклучоци</option>
          </select>
        </div>
        <div className="date-filter-group">
          <label>Од:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <label>До:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button className="button button-primary" onClick={handleApplyFilters}>
          Примени
        </button>
        <button className="button button-outline" onClick={handleResetFilters}>
          Ресетирај
        </button>
        <button className="button button-outline" onClick={exportErrors}>
          Извези CSV
        </button>
        <button className="button button-outline" onClick={fetchErrors}>
          🔄 Освежи
        </button>
        <button
          className="button button-danger"
          onClick={async () => {
            try {
              // Try using the client first
              try {
                const { reportError } = await import('../api/client');
                await reportError(new Error("Manual test error from Admin panel"));
                alert("✅ Success! Error reported via client. Click Refresh.");
              } catch (clientError) {
                console.error("Client report failed, trying direct fetch:", clientError);

                // Fallback to direct fetch
                const response = await fetch('http://localhost:8000/api/errors/', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    type: 'MANUAL_TEST',
                    message: 'Direct fetch fallback error',
                    endpoint: '/admin/test-button',
                    status: 0
                  })
                });

                if (response.ok) {
                  alert("⚠️ Client failed but Direct Fetch succeeded! Click Refresh.");
                } else {
                  const text = await response.text();
                  alert(`❌ All attempts failed. Direct fetch status: ${response.status}\nResponse: ${text}`);
                }
              }

              fetchErrors();
            } catch (e) {
              console.error("Critical failure in test button", e);
              alert(`❌ Critical error: ${e.message}`);
            }
          }}
          style={{ marginLeft: 'auto', borderColor: '#ef4444', color: '#ef4444' }}
        >
          ⚠️ Test Error
        </button>
      </div>

      <div className="errors-summary">
        <div className="summary-card">
          <div className="summary-label">Вкупно грешки</div>
          <div className="summary-value">{errors.reduce((sum, e) => sum + e.count, 0)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Уникатни грешки</div>
          <div className="summary-value">{errors.length}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">API грешки</div>
          <div className="summary-value">
            {errors.filter((e) => e.type === 'API_ERROR').reduce((sum, e) => sum + e.count, 0)}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Timeout грешки</div>
          <div className="summary-value">
            {errors.filter((e) => e.type === 'TIMEOUT').reduce((sum, e) => sum + e.count, 0)}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Исклучоци</div>
          <div className="summary-value">
            {errors.filter((e) => e.type === 'EXCEPTION').reduce((sum, e) => sum + e.count, 0)}
          </div>
        </div>
      </div>

      <div className="errors-list">
        <div className="list-header">
          <span>Тип</span>
          <span>Endpoint</span>
          <span>Status</span>
          <span>Порака</span>
          <span>Корисник</span>
          <span>Време</span>
          <span>Број</span>
          <span>Детали</span>
        </div>
        {loading ? (
          <div className="empty-state">Се вчитува...</div>
        ) : filteredErrors.length === 0 ? (
          <div className="empty-state">Нема грешки</div>
        ) : (
          filteredErrors.map((error) => (
            <div key={error.id} className="error-row">
              <span className={`error-type error-type-${error.type}`}>{error.type}</span>
              <span className="error-endpoint" title={error.endpoint}>{error.endpoint || 'N/A'}</span>
              <span className={`error-status status-${Math.floor((error.status || 0) / 100)}`}>
                {error.status || 'N/A'}
              </span>
              <span className="error-message" title={error.message}>{error.message}</span>
              <span className="error-user">{error.username || 'Anonymous'}</span>
              <span className="error-time">
                {new Date(error.timestamp).toLocaleString('mk-MK')}
              </span>
              <span className="error-count">{error.count}x</span>
              <span className="error-actions">
                <button
                  className="button-link"
                  onClick={() => setSelectedError(error)}
                  title="Погледни детали"
                >
                  👁️
                </button>
              </span>
            </div>
          ))
        )}
      </div>

      {selectedError && (
        <div className="modal-overlay" onClick={() => setSelectedError(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Детали за грешка</h2>
              <button className="modal-close" onClick={() => setSelectedError(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="error-detail-row">
                <strong>Тип:</strong>
                <span className={`error-type error-type-${selectedError.type}`}>
                  {selectedError.type}
                </span>
              </div>
              <div className="error-detail-row">
                <strong>Endpoint:</strong>
                <span>{selectedError.endpoint || 'N/A'}</span>
              </div>
              <div className="error-detail-row">
                <strong>HTTP Status:</strong>
                <span className={`error-status status-${Math.floor((selectedError.status || 0) / 100)}`}>
                  {selectedError.status || 'N/A'}
                </span>
              </div>
              <div className="error-detail-row">
                <strong>Корисник:</strong>
                <span>{selectedError.username || 'Anonymous'}</span>
              </div>
              <div className="error-detail-row">
                <strong>Број на појавувања:</strong>
                <span>{selectedError.count}x</span>
              </div>
              <div className="error-detail-row">
                <strong>Последна појава:</strong>
                <span>{new Date(selectedError.timestamp).toLocaleString('mk-MK')}</span>
              </div>
              <div className="error-detail-section">
                <strong>Порака:</strong>
                <pre className="error-detail-message">{selectedError.message}</pre>
              </div>
              {selectedError.stack_trace && (
                <div className="error-detail-section">
                  <strong>Stack Trace:</strong>
                  <pre className="error-detail-stack">{selectedError.stack_trace}</pre>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="button button-primary" onClick={() => setSelectedError(null)}>
                Затвори
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
